import { AccessSpace, VisibilityCategory } from '../../iam/access/types';
import { CompanyType } from '../input/companies/types';
import { MeasurableGroupType } from '../input/measurable-group/types';
import { ModelEntityReference, ModelEntityType } from '../input/types';

// Should only contain the minimal info used for displaying the search hit
export interface ModelEntitySearchResponseItem {
  id: string;
  name: string;
  description?: string;
  country?: string;
  type: ModelEntityType;
  companyType?: CompanyType; // Companies only
  groupType?: MeasurableGroupType; // Groupfolios only
  query?: string;
  score?: number;
  accessSpace?: AccessSpace; // Company groups only: Needed to separate shared fund ODAI funds from customer funds. -OH
  source?: string | undefined; // Company groups only
  modelledBy?: { id: string; name: string }; // Legal entities only.
  foundVia?: { id: string; name: string }; // Companies only.
  // "teaser" here does NOT mean that user has READ_TEASER access. It means that user has SEARCH access but no at least READ_TEASER access. -ML
  teaser?: boolean;
  created?: boolean;
  fuzzyProductSetQuality?: string;
  listingStatus?: string; // Companies only — e.g. "PUBLIC" or "PRIVATE"
  visibility?: VisibilityCategory;
}

export interface SearchAttempts {
  attempts: string[];
  searchType?: HeaderSearchSelection; // Only filled in header search, elsewhere left empty.
  entityRef?: ModelEntityReference;
}

// This is the type of the elements in the list that specifies the search targets.
export type SearchEntityType = ModelEntityType | 'legalEntity';

// NOTE: The order matters! The types are ordered by specificity, used when removing duplicate hits. (e.g. from both companies and legal entities pointing to same id)
export const allSearchTypes = [
  ModelEntityType.PRODUCT,
  ModelEntityType.PHENOMENON,
  ModelEntityType.MIXIN,
  ModelEntityType.GROUP,
  ModelEntityType.PORTFOLIO,
  ModelEntityType.COMPANY,
  'legalEntity',
] as const;

export enum AdditionalSearchType {
  ALL = 'all',
  ALL_INTERNAL = 'all_internal',
}

// Use this type when you wish to refer to selection done by user when searching
export type HeaderSearchSelection =
  | AdditionalSearchType.ALL
  | ModelEntityType
  | 'legalEntity'
  | AdditionalSearchType.ALL_INTERNAL;

export const defaultAllSearchScope = [
  ModelEntityType.COMPANY,
  ModelEntityType.GROUP,
  ModelEntityType.PORTFOLIO,
  ModelEntityType.PRODUCT,
  ModelEntityType.MIXIN,
  ModelEntityType.PHENOMENON,
] as const;

export const internalAllSearchScope = allSearchTypes;

export const allSemanticSearchTypes = [
  ModelEntityType.PRODUCT,
  ModelEntityType.PHENOMENON,
  ModelEntityType.MIXIN,
] as const;

export function searchTypeHasSemanticSupport(
  searchType: SearchEntityType,
): searchType is (typeof allSemanticSearchTypes)[number] {
  return allSemanticSearchTypes.includes(searchType as (typeof allSemanticSearchTypes)[number]);
}

export type SemanticProductSearchPartition = (typeof allSemanticSearchTypes)[number];
